import React from "react";
import { observer } from "mobx-react";
import { AuthorComposerCreateStore } from "../../store";
import { AuthorComposerPublicCreateForm } from "./AuthorComposerPublicCreateForm";
import { ErrorDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { Gender } from "../../../../api/graphql";
import { TaxIdType } from "../../../../api/graphql";

export type AuthorComposerPublicCreateProps = {
  authorComposerCreateStore: AuthorComposerCreateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const AuthorComposerPublicCreate: React.FC<AuthorComposerPublicCreateProps> =
  observer(
    ({ authorComposerCreateStore, showToolbox = true, showSaveFooter }) => {
      return (
        <>
          <SavingOverlay open={authorComposerCreateStore.isSaving} />
          <AuthorComposerPublicCreateForm
            authorComposerCreateStore={authorComposerCreateStore}
            firstNameError={authorComposerCreateStore.errors.firstName?.message}
            onFirstNameChange={(value: string) => {
              authorComposerCreateStore.setFirstNameValue(value);
            }}
            firstName={authorComposerCreateStore.entityValues.firstName}
            lastNameError={authorComposerCreateStore.errors.lastName?.message}
            onLastNameChange={(value: string) => {
              authorComposerCreateStore.setLastNameValue(value);
            }}
            lastName={authorComposerCreateStore.entityValues.lastName}
            genderError={authorComposerCreateStore.errors.gender?.message}
            genderOptions={authorComposerCreateStore.getGenderOptions()}
            onGenderChange={(value: Gender | "") => {
              authorComposerCreateStore.setGenderValue(value);
            }}
            gender={authorComposerCreateStore.entityValues.gender}
            taxIdTypeError={authorComposerCreateStore.errors.taxIdType?.message}
            taxIdTypeOptions={authorComposerCreateStore.getTaxIdTypeOptions()}
            onTaxIdTypeChange={(value: TaxIdType | "") => {
              authorComposerCreateStore.setTaxIdTypeValue(value);
            }}
            taxIdType={authorComposerCreateStore.entityValues.taxIdType}
            taxIdError={authorComposerCreateStore.errors.taxId?.message}
            onTaxIdChange={(value: string) => {
              authorComposerCreateStore.setTaxIdValue(value);
            }}
            taxId={authorComposerCreateStore.entityValues.taxId}
            emailError={authorComposerCreateStore.errors.email?.message}
            onEmailChange={(value: string) => {
              authorComposerCreateStore.setEmailValue(value);
            }}
            email={authorComposerCreateStore.entityValues.email}
            phoneError={authorComposerCreateStore.errors.phone?.message}
            onPhoneChange={(value: string) => {
              authorComposerCreateStore.setPhoneValue(value);
            }}
            phone={authorComposerCreateStore.entityValues.phone}
            nationalityError={
              authorComposerCreateStore.errors.nationality?.message
            }
            onNationalityChange={(value) => {
              authorComposerCreateStore.setNationalityValue(value);
            }}
            nationalitySelectedValue={
              authorComposerCreateStore.entityValues.showNationality
            }
            onNationalitySelected={(value) => {
              authorComposerCreateStore.setSelectedNationality(value);
            }}
            nationalityOptions={authorComposerCreateStore.getNationalityOptions()}
            dateOfBirthError={
              authorComposerCreateStore.errors.dateOfBirth?.message
            }
            onDateOfBirthChange={(value) => {
              authorComposerCreateStore.setDateOfBirthValue(value);
            }}
            dateOfBirth={authorComposerCreateStore.entityValues.dateOfBirth}
            streetAndNumberError={
              authorComposerCreateStore.errors.streetAndNumber?.message
            }
            onStreetAndNumberChange={(value: string) => {
              authorComposerCreateStore.setStreetAndNumberValue(value);
            }}
            streetAndNumber={
              authorComposerCreateStore.entityValues.streetAndNumber
            }
            floorError={authorComposerCreateStore.errors.floor?.message}
            onFloorChange={(value: string) => {
              authorComposerCreateStore.setFloorValue(value);
            }}
            floor={authorComposerCreateStore.entityValues.floor}
            suiteError={authorComposerCreateStore.errors.suite?.message}
            onSuiteChange={(value: string) => {
              authorComposerCreateStore.setSuiteValue(value);
            }}
            suite={authorComposerCreateStore.entityValues.suite}
            postalCodeError={
              authorComposerCreateStore.errors.postalCode?.message
            }
            onPostalCodeChange={(value: string) => {
              authorComposerCreateStore.setPostalCodeValue(value);
            }}
            postalCode={authorComposerCreateStore.entityValues.postalCode}
            countryError={authorComposerCreateStore.errors.country?.message}
            onCountryChange={(value) => {
              authorComposerCreateStore.setCountryValue(value);
            }}
            countrySelectedValue={
              authorComposerCreateStore.entityValues.showCountry
            }
            onCountrySelected={(value) => {
              authorComposerCreateStore.setSelectedCountry(value);
            }}
            countryOptions={authorComposerCreateStore.getCountryOptions()}
            provinceError={authorComposerCreateStore.errors.province?.message}
            onProvinceChange={(value) => {
              authorComposerCreateStore.setProvinceValue(value);
            }}
            provinceSelectedValue={
              authorComposerCreateStore.entityValues.showProvince
            }
            onProvinceSelected={(value) => {
              authorComposerCreateStore.setSelectedProvince(value);
            }}
            provinceOptions={authorComposerCreateStore.getProvinceOptions()}
            cityError={authorComposerCreateStore.errors.city?.message}
            onCityChange={(value: string) => {
              authorComposerCreateStore.setCityValue(value);
            }}
            city={authorComposerCreateStore.entityValues.city}
            akaError={authorComposerCreateStore.errors.aka?.message}
            onAkaChange={(value: string) => {
              authorComposerCreateStore.setAkaValue(value);
            }}
            aka={authorComposerCreateStore.entityValues.aka}
            ipiError={authorComposerCreateStore.errors.ipi?.message}
            onIpiChange={(value: string) => {
              authorComposerCreateStore.setIpiValue(value);
            }}
            ipi={authorComposerCreateStore.entityValues.ipi}
            showToolbox={showToolbox}
            showSaveFooter={showSaveFooter}
            saving={authorComposerCreateStore.isProcessing}
            onSave={() => authorComposerCreateStore.saveNewAuthorComposer()}
            onCancel={() => authorComposerCreateStore.cancelNewAuthorComposer()}
          />
          <ErrorDialog
            open={authorComposerCreateStore.errorDialogOpened}
            title={authorComposerCreateStore.errorTitle}
            description={authorComposerCreateStore.errorDescription}
            onClose={() => authorComposerCreateStore.closeErrorDialog()}
          />
        </>
      );
    }
  );
